import { useEffect, useState } from 'react'
import { filterSlashItems, groupSlashItems, slashItems, slashMatch } from '../../core/slash-menu'
import { SlashMenuList } from '../../slash-menu-list'
import type { Overlay } from '../view-api'
import { OverlayPortal, useDismiss, usePosition, type OverlayApi } from './shared'

export const SlashMenu: Overlay = ({ api }: { api: OverlayApi }) => {
  const [dismissed, setDismissed] = useState<string | null>(null)
  const [highlight, setHighlight] = useState<{ key: string | null; index: number }>({ key: null, index: 0 })
  const [panel, setPanel] = useState<HTMLDivElement | null>(null)
  const position = usePosition(api, undefined, false, panel, { triggerLength: (slashMatch(api.state)?.query.length ?? 0) + 1, gap: 3 })
  const testId = api.testIds?.slashMenu ?? 'content-blocks-editor-slash-menu'
  const match = api.mode === 'edit' ? slashMatch(api.state) : null
  const key = match ? `${match.row.block.id}:${match.start}:${match.query}` : null
  const hostItems = (api.slashMenuItems ?? []).map(item => Object.assign({}, item, { section: item.section ?? 'More' as const }))
  // Display order (grouped by section) is the keyboard order: a host item in an earlier section lists among that section's items.
  const items = groupSlashItems(filterSlashItems([...slashItems, ...hostItems], match?.query ?? '')).flatMap(group => group.items.map(entry => entry.item))
  // Dismissal belongs to one trigger instance: once the trigger is gone, a freshly typed one opens again.
  if (key === null && dismissed !== null) setDismissed(null)
  const open = match !== null && dismissed !== key
  useDismiss(open, () => { setDismissed(key) }, { current: panel })
  const index = highlight.key === key ? highlight.index : 0
  const setIndex = (index: number) => { setHighlight({ key, index }) }
  function choose(item: typeof items[number]) {
    if ('action' in item) {
      const result = api.dispatch({ type: 'applySlashItem', title: item.title })
      const action = item.action
      if (action.kind === 'link') api.root?.dispatchEvent(new CustomEvent('core-view-open-link', { detail: result.state.selection }))
      if (action.kind === 'files') api.root?.dispatchEvent(new CustomEvent('core-view-insert-files', { detail: action.accept }))
    } else {
      api.dispatch({ type: 'applySlashItem', title: item.title, block: item.block?.('') ?? null })
      item.onSelect?.()
    }
    setDismissed(key)
    api.root?.focus()
  }
  useEffect(() => {
    const root = api.root
    if (!open || !root) return () => {}
    const keydown = (event: KeyboardEvent) => {
      if (!(event.target instanceof Node) || !root.contains(event.target)) return
      if (!['ArrowDown', 'ArrowUp', 'Enter', 'Tab', 'Escape'].includes(event.key) || event.isComposing) return
      event.preventDefault()
      event.stopImmediatePropagation()
      if (event.key === 'Escape') setDismissed(key)
      else if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
        setIndex((index + (event.key === 'ArrowDown' ? 1 : -1) + items.length) % Math.max(1, items.length))
      } else if (items[index]) choose(items[index])
    }
    document.addEventListener('keydown', keydown, true)
    return () => { document.removeEventListener('keydown', keydown, true) }
  })
  useEffect(() => {
    panel?.querySelectorAll('[role="option"]')[index]?.scrollIntoView({ block: 'nearest' })
  }, [index, panel])
  if (!open) return null
  return <OverlayPortal api={api}><div role="presentation" ref={setPanel} style={position} onMouseDown={event => { event.preventDefault() }}>
    <SlashMenuList testId={testId} items={items} selectedIndex={index} onHighlight={setIndex}
      onChoose={index => { if (items[index]) choose(items[index]) }} />
  </div></OverlayPortal>
}
